import { FetchArgs } from '@reduxjs/toolkit/dist/query'
import { AuthToken, AuthTokenArgs } from '@typesApp/Customer'

export const GUEST_AUTH_HEADER_NAME = {
  CONTENT_TYPE: 'Content-Type',
  AUTHORIZATION: 'Authorization',
  CUSTOMER_NUMBER: 'x-directbilling-customer-number',
}

type GuestReauthService = {
  fetchAuthToken: (args: AuthTokenArgs) => Promise<AuthToken>
}

type GuestReauthController = {
  /**
   * Reauthorize by re-fetching guest auth token
   * @returns updated fetch args with new guest auth token
   */
  reauthOrThrow: () => Promise<FetchArgs>
}

export const guestReauthController = (service: GuestReauthService, args: string | FetchArgs): GuestReauthController => {
  const reauthOrThrow = async (): Promise<FetchArgs> => {
    if (!isFetchArgs(args)) {
      throw new Error('Expected fetch args for guest reauth')
    }

    if (!args?.params?.ecommerce) {
      throw new Error('Ecommerce param required for guest auth token')
    }

    if (!args.headers?.[GUEST_AUTH_HEADER_NAME.CUSTOMER_NUMBER]) {
      throw new Error('Customer number header required for guest auth token')
    }

    const ecommerce = args?.params?.ecommerce
    const guestCustomerNumber = args.headers?.[GUEST_AUTH_HEADER_NAME.CUSTOMER_NUMBER]
    if (!guestCustomerNumber || !ecommerce) {
      throw new Error('Customer number and ecommerce required for guest auth token')
    }

    const authToken = await reauthGuest(ecommerce, guestCustomerNumber)
    return updateFetchArgsWithToken(args, authToken)
  }

  const reauthGuest = async (ecommerce: string, customerNumber: string): Promise<AuthToken> => {
    // refetch guest auth token
    const tokenArgs: AuthTokenArgs = {
      ecommerce,
      customerNumber,
      customerType: 'GUEST',
    }

    return await service.fetchAuthToken(tokenArgs)
  }

  const updateFetchArgsWithToken = (fetchArgs: FetchArgs, token: AuthToken): FetchArgs => {
    const result = {
      ...fetchArgs,
      headers: {
        ...fetchArgs.headers,
        [GUEST_AUTH_HEADER_NAME.AUTHORIZATION]: `Bearer ${token.accessToken}`,
      },
    }

    if (result?.params?.ecommerce) {
      delete result.params.ecommerce
    }

    return result
  }

  return {
    reauthOrThrow,
  }
}

const isFetchArgs = (args: string | FetchArgs): args is FetchArgs => {
  return typeof args === 'object' && (args?.headers || args?.body || args?.params)
}

export const copyFetchArgsWithoutEcommerceParam = (args: string | FetchArgs): string | FetchArgs => {
  const argsCopy = isFetchArgs(args) ? { ...args } : args

  if (isFetchArgs(argsCopy)) {
    // avoid sending ecommerce params to b/e as it is only used for guest token reauth
    if (argsCopy?.params?.ecommerce) {
      const { ecommerce, ...restParams } = argsCopy.params
      argsCopy.params = restParams
    }
  }
  return argsCopy
}
